'use client';

import { useCallback } from 'react';
import { useComparer } from '../../hooks/useComparer';
import {
  HttpComparerProvider as DesignSystemHttpComparerProvider,
  type HttpComparerData,
} from '@terra/ui-design-system';

/**
 * Global provider that enables "Send to Comparer" functionality from design system components.
 * Wraps the design system HttpComparerProvider and connects it to the terra-proxy comparer.
 */
export function HttpComparerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { addRequestToComparer } = useComparer();

  const handleSendToComparer = useCallback(
    (data: HttpComparerData) => {
      // Determine if this is a request or response based on the data
      const isResponse = data.type === 'response' || data.status !== undefined;

      // Create a request-like object that useComparer expects
      const requestLike = {
        id: `comparer-${Date.now()}`,
        method: data.method,
        url: data.url,
        headers: data.headers || {},
        body: data.body || '',
        httpVersion: data.httpVersion,
        timestamp: new Date(),
        // For responses, include response-specific fields
        ...(isResponse && {
          status: data.status,
          responseHeaders: data.responseHeaders,
          responseBody: data.responseBody,
        }),
      };

      addRequestToComparer(
        requestLike,
        data.label,
        isResponse ? 'response' : 'request',
      );
    },
    [addRequestToComparer],
  );

  return (
    <DesignSystemHttpComparerProvider onSendToComparer={handleSendToComparer}>
      {children}
    </DesignSystemHttpComparerProvider>
  );
}
